import { useState, useRef, useEffect } from 'react';
import type { Category } from '@/mocks/categories';

interface CategorySectionProps {
  categories: Category[];
}

const categoryImages: Record<string, string> = {
  'Shoes': 'https://readdy.ai/api/search-image?query=A%20single%20pair%20of%20premium%20white%20leather%20sneakers%20on%20a%20clean%20cream%20colored%20surface%20soft%20natural%20window%20light%20minimal%20product%20photography%20with%20gentle%20shadows%20elegant%20composition%20no%20background%20clutter&width=400&height=300&seq=cat-shoes&orientation=landscape',
  'Hoodies / Sweaters': 'https://readdy.ai/api/search-image?query=A%20neatly%20folded%20soft%20beige%20heavyweight%20cotton%20hoodie%20on%20a%20clean%20cream%20surface%20warm%20natural%20lighting%20minimal%20product%20photography%20cozy%20aesthetic%20gentle%20shadows%20clean%20composition&width=400&height=300&seq=cat-hoodies&orientation=landscape',
  'T-Shirts': 'https://readdy.ai/api/search-image?query=A%20premium%20white%20cotton%20oversized%20tshirt%20folded%20neatly%20on%20a%20cream%20colored%20surface%20soft%20daylight%20minimal%20product%20photography%20clean%20aesthetic%20with%20subtle%20texture%20gentle%20shadows&width=400&height=300&seq=cat-tshirts&orientation=landscape',
  'Jackets': 'https://readdy.ai/api/search-image?query=A%20stylish%20olive%20green%20lightweight%20bomber%20jacket%20laid%20flat%20on%20a%20cream%20surface%20soft%20natural%20lighting%20editorial%20product%20photography%20minimal%20composition%20with%20clean%20lines%20and%20subtle%20shadows&width=400&height=300&seq=cat-jackets&orientation=landscape',
  'Pants / Shorts': 'https://readdy.ai/api/search-image?query=A%20pair%20of%20well%20tailored%20beige%20cargo%20pants%20folded%20neatly%20on%20a%20cream%20surface%20soft%20daylight%20minimal%20product%20photography%20clean%20lines%20relaxed%20aesthetic%20gentle%20shadows&width=400&height=300&seq=cat-pants&orientation=landscape',
  'Headwear': 'https://readdy.ai/api/search-image?query=A%20stylish%20neutral%20toned%20baseball%20cap%20placed%20on%20a%20cream%20surface%20soft%20natural%20light%20minimal%20product%20photography%20clean%20composition%20with%20subtle%20shadow%20simple%20elegant%20aesthetic&width=400&height=300&seq=cat-headwear&orientation=landscape',
  'Sets': 'https://readdy.ai/api/search-image?query=A%20coordinated%20matching%20hoodie%20and%20sweatpants%20set%20in%20soft%20gray%20folded%20together%20on%20a%20cream%20surface%20warm%20natural%20lighting%20minimal%20product%20photography%20clean%20aesthetic%20gentle%20shadows&width=400&height=300&seq=cat-sets&orientation=landscape',
  'Underwear / Underpants': 'https://readdy.ai/api/search-image?query=Soft%20cotton%20underwear%20basics%20folded%20neatly%20in%20neutral%20tones%20on%20a%20cream%20surface%20clean%20minimal%20product%20photography%20soft%20natural%20lighting%20simple%20elegant%20composition%20with%20subtle%20shadows&width=400&height=300&seq=cat-underwear&orientation=landscape',
  'Jersey': 'https://readdy.ai/api/search-image?query=A%20stylish%20sports%20jersey%20in%20deep%20forest%20green%20laid%20flat%20on%20a%20cream%20surface%20soft%20natural%20light%20minimal%20product%20photography%20clean%20athletic%20aesthetic%20with%20gentle%20shadows&width=400&height=300&seq=cat-jersey&orientation=landscape',
  'Accessories': 'https://readdy.ai/api/search-image?query=A%20curated%20arrangement%20of%20minimalist%20accessories%20including%20a%20leather%20watch%20and%20simple%20bracelet%20on%20a%20cream%20surface%20soft%20natural%20lighting%20editorial%20product%20photography%20clean%20elegant%20composition&width=400&height=300&seq=cat-accessories&orientation=landscape',
  'Bags': 'https://readdy.ai/api/search-image?query=A%20premium%20canvas%20tote%20bag%20in%20warm%20beige%20tone%20standing%20on%20a%20cream%20surface%20soft%20daylight%20minimal%20product%20photography%20clean%20aesthetic%20with%20subtle%20shadows%20elegant%20simplicity&width=400&height=300&seq=cat-bags&orientation=landscape',
  'Belts': 'https://readdy.ai/api/search-image?query=A%20quality%20leather%20belt%20with%20a%20simple%20metal%20buckle%20coiled%20on%20a%20cream%20surface%20soft%20natural%20lighting%20minimal%20product%20photography%20clean%20composition%20with%20subtle%20shadows%20elegant%20aesthetic&width=400&height=300&seq=cat-belts&orientation=landscape',
  'Perfumes': 'https://readdy.ai/api/search-image?query=A%20minimalist%20glass%20perfume%20bottle%20with%20amber%20colored%20fragrance%20on%20a%20cream%20surface%20soft%20natural%20light%20elegant%20product%20photography%20clean%20composition%20with%20subtle%20reflections%20and%20gentle%20shadows&width=400&height=300&seq=cat-perfumes&orientation=landscape',
  'Other Stuff': 'https://readdy.ai/api/search-image?query=A%20curated%20assortment%20of%20lifestyle%20accessories%20including%20a%20keychain%20a%20phone%20case%20and%20a%20small%20notebook%20on%20a%20cream%20surface%20soft%20natural%20lighting%20clean%20minimal%20product%20photography%20warm%20aesthetic%20with%20gentle%20shadows&width=400&height=300&seq=cat-other&orientation=landscape',
};

export default function CategorySection({ categories }: CategorySectionProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('Browse by Category');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (cat: Category) => {
    setSelectedCategory(cat.name);
    setIsOpen(false);
    window.open(cat.url, '_blank', 'noopener,nofollow');
  };

  const handleGridClick = (url: string) => {
    window.open(url, '_blank', 'noopener,nofollow');
  };

  return (
    <section className="py-14 md:py-20 px-4 md:px-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-4xl text-foreground-950 mb-3">
            Explore Sugargoo Spreadsheet Categories
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
            Browse our organized product collections and discover trending finds across every category
          </p>
        </div>

        <div className="flex justify-center mb-12" ref={dropdownRef}>
          <div className="relative w-full max-w-md">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="w-full flex items-center justify-between bg-background-50 border border-background-200 rounded-lg px-5 py-3.5 text-foreground-800 text-sm cursor-pointer hover:border-primary-300 transition-colors duration-200"
            >
              <span className="flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-grid-line text-foreground-500"></i>
                </span>
                {selectedCategory}
              </span>
              <span className="w-5 h-5 flex items-center justify-center">
                {isOpen ? (
                  <i className="ri-arrow-up-s-line text-foreground-400 transition-transform duration-200"></i>
                ) : (
                  <i className="ri-arrow-down-s-line text-foreground-400 transition-transform duration-200"></i>
                )}
              </span>
            </button>

            {isOpen && (
              <div className="absolute z-50 mt-2 w-full bg-background-50 border border-background-200 rounded-lg overflow-hidden max-h-80 overflow-y-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => handleSelect(cat)}
                    className="w-full flex items-center gap-3 px-5 py-3 text-left text-sm text-foreground-700 hover:bg-primary-50/60 cursor-pointer transition-colors duration-150 whitespace-nowrap"
                  >
                    <span className="w-5 h-5 flex items-center justify-center">
                      <i className={`${cat.icon} text-primary-500`}></i>
                    </span>
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
          {categories.map((cat) => (
            <button
              key={cat.name}
              onClick={() => handleGridClick(cat.url)}
              className="group flex flex-col bg-background-50 border border-background-200/70 rounded-lg overflow-hidden cursor-pointer hover:border-primary-300 transition-all duration-200"
            >
              <div className="w-full aspect-[4/3] overflow-hidden">
                <img
                  src={categoryImages[cat.name] || categoryImages['Other Stuff']}
                  alt={`${cat.name} category products`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="flex items-center gap-2 px-3 py-2.5">
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-100/70 text-primary-600 flex-shrink-0">
                  <i className={`${cat.icon} text-[10px]`}></i>
                </span>
                <span className="text-xs text-foreground-700 font-medium text-left whitespace-nowrap truncate">
                  {cat.name}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}